import { Alert, Button, Typography } from "@mui/material";

export interface SelectAllBannerProps {
  /** Number of items currently selected */
  selectedCount: number;
  /** Number of items on current page that are selected */
  pageSelectedCount: number;
  /** Total number of items matching current filters */
  totalFilteredCount: number;
  /** Whether all filtered items are selected */
  isAllFilteredSelected: boolean;
  /** Callback when "Select all X matching filters" is clicked */
  onSelectAllFiltered: () => void;
  /** Callback when "Clear selection" is clicked */
  onClearSelection: () => void;
}

/**
 * Banner that appears when all items on a page are selected,
 * offering to select all items matching the current filters
 */
export function SelectAllBanner({
  selectedCount,
  pageSelectedCount,
  totalFilteredCount,
  isAllFilteredSelected,
  onSelectAllFiltered,
  onClearSelection,
}: SelectAllBannerProps) {
  // Don't show if nothing selected or not all page items selected
  if (selectedCount === 0 || pageSelectedCount === 0) {
    return null;
  }

  // Show "all X selected" message when all filtered items are selected
  if (isAllFilteredSelected) {
    return (
      <Alert
        severity="info"
        sx={{
          borderRadius: 0,
          py: 0.5,
          "& .MuiAlert-message": {
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            justifyContent: "center",
          },
        }}
      >
        <Typography variant="body2">
          All {totalFilteredCount.toLocaleString()} documents matching filters are selected.
        </Typography>
        <Button size="small" onClick={onClearSelection} sx={{ textTransform: "none" }}>
          Clear selection
        </Button>
      </Alert>
    );
  }

  // Show option to select all when all page items are selected
  // but not all filtered items
  const allPageSelected = pageSelectedCount === selectedCount;
  if (allPageSelected && totalFilteredCount > selectedCount) {
    return (
      <Alert
        severity="info"
        sx={{
          borderRadius: 0,
          py: 0.5,
          "& .MuiAlert-message": {
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: "100%",
            justifyContent: "center",
          },
        }}
      >
        <Typography variant="body2">
          All {selectedCount.toLocaleString()} documents on this page are selected.
        </Typography>
        <Button size="small" onClick={onSelectAllFiltered} sx={{ textTransform: "none" }}>
          Select all {totalFilteredCount.toLocaleString()} matching filters
        </Button>
        <Button size="small" onClick={onClearSelection} sx={{ textTransform: "none" }}>
          Clear selection
        </Button>
      </Alert>
    );
  }

  return null;
}
